import React, {FC, useMemo} from 'react'
import {PurchaseOrder, RequisitionLine} from '../core/_models'
import {DocBlock, DocField, DocFieldGrid, DocNote} from './ProcurementDocumentSections'
import {parseQuotationTerms} from '../core/quotationTerms'
import ProcurementAttachmentPrintList from './ProcurementAttachmentPrintList'
import {useProcurementBrandingAssets} from './ProcurementDocumentBranding'
import MoneyDisplay from './MoneyDisplay'
import {useAuth} from '../../auth'
import {
  getContextHeaderLabels,
  getCompanyNameFromEntity,
  getEquipmentNameFromLine,
  getItemDisplayNameFromLine,
  getLinkedDefectDisplay,
  getSubcomponentNameFromLine,
  getVesselNameFromEntity,
  resolveRequisitionViewContext,
  uniqueJoinedValues,
} from '../core/technicalItemContext'
import {resolvePurchaseOrderMoneyPresentation, resolvePurchaseOrderOriginalCurrency} from '../core/purchaseOrderMoney'

interface PurchaseOrderPARUDocumentProps {
  purchaseOrder: PurchaseOrder
  vendorLogisticsChangeRequestContent?: React.ReactNode
}



const numberOrZero = (v: any) => {
  const numeric = Number(v)
  return Number.isFinite(numeric) ? numeric : 0
}
const isMeaningfulFinancialValue = (value: unknown) => {
  const numeric = Number(value)
  // Values below half a cent render as 0.00 and should not create a row.
  // Negative adjustments remain visible when they are financially meaningful.
  return Number.isFinite(numeric) && Math.abs(numeric) >= 0.005
}
const readFiniteNumber = (value: unknown): number | null => {
  if (value == null || value === '') {
    return null
  }
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}
const firstFiniteNumber = (...values: unknown[]): number | null => {
  for (const value of values) {
    const numeric = readFiniteNumber(value)
    if (numeric != null) {
      return numeric
    }
  }
  return null
}
const sumFiniteNumbers = (values: unknown[]): number | null => {
  let sum = 0
  let found = false
  for (const value of values) {
    const numeric = readFiniteNumber(value)
    if (numeric == null) {
      continue
    }
    sum += numeric
    found = true
  }
  return found ? sum : null
}
const calculateLineTotal = (
  subtotal: number | null,
  discountPercent: number,
  gstPercent: number
): number | null => {
  if (subtotal == null) {
    return null
  }
  const discountAmount = subtotal * (discountPercent / 100)
  const afterDiscount = subtotal - discountAmount
  return afterDiscount + afterDiscount * (gstPercent / 100)
}
const formatFallbackMoney = (amount: number | null, currency: string | null) => {
  if (amount == null) {
    return '-'
  }
  const code = String(currency || '').trim().toUpperCase()
  try {
    if (code) {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: code,
        maximumFractionDigits: 2,
      }).format(amount)
    }
  } catch {
    // Fall back to generic formatting.
  }
  return code
    ? `${code} ${amount.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
    : amount.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})
}
const resolveLineAccountCode = (po: PurchaseOrder, line?: any) => {
  const lineAccountCode = String(line?.accountCode || '').trim()
  if (lineAccountCode) return lineAccountCode

  const commonAccountCode = String(po?.budgetCode || '').trim()
  if (commonAccountCode) return commonAccountCode

  const logisticAccountCode = String(po?.logisticAccountCode || '').trim()
  if (logisticAccountCode) return logisticAccountCode

  return ''
}

const resolveLines = (po: PurchaseOrder): RequisitionLine[] => {
  const quotationLines = Array.isArray(po.quotationLines)
    ? po.quotationLines
    : []

  const requisitionLines = (po as any)?.rfq?.requisition?.lines as
    | RequisitionLine[]
    | undefined

  const reqById = new Map<number, any>()

  ;(requisitionLines || []).forEach((line: any, idx: number) => {
    if (line?.id != null) {
      reqById.set(Number(line.id), {
        ...line,
        __idx: idx,
      })
    }
  })

  /*
   * Preferred path:
   * PO has quotation lines, so preserve the vendor quotation values exactly.
   */
  if (quotationLines.length > 0) {
    return quotationLines.map((line: any, idx: number) => {
      const reqLine =
        (line?.requisitionLineId != null
          ? reqById.get(Number(line.requisitionLineId))
          : null) ||
        ((requisitionLines || [])[idx] as any) ||
        null

      const originalUnitPrice =
        line?.originalUnitPrice ??
        line?.unitPrice ??
        reqLine?.estimatedUnitPrice ??
        null

      const originalLineSubtotal =
        line?.originalLineSubtotal ??
        line?.selectedOriginalSubtotal ??
        null

      const originalLineTax =
        line?.originalLineTax ??
        line?.selectedOriginalTax ??
        null

      const originalLineTotal =
        line?.originalLineTotal ??
        line?.selectedOriginalTotal ??
        null

      const companyLineSubtotal =
        line?.companyLineSubtotal ??
        line?.selectedCompanySubtotal ??
        null

      const companyLineTax =
        line?.companyLineTax ??
        line?.selectedCompanyTax ??
        null

      const companyLineTotal =
        line?.companyLineTotal ??
        line?.selectedCompanyTotal ??
        null

      return {
        id:
          line?.requisitionLineId ??
          reqLine?.id ??
          idx + 1,

        manualItemName:
          reqLine?.manualItemName ||
          reqLine?.part?.name ||
          reqLine?.itemName ||
          line?.lineItemName ||
          line?.manualItemName ||
          po.description ||
          `PO Item ${idx + 1}`,

        partId:
          reqLine?.partId ??
          line?.partId ??
          null,

        quantity:
          line?.assignedQuantity ??
          line?.quantity ??
          reqLine?.quantity ??
          0,

        unitOfMeasurement:
          reqLine?.unitOfMeasurement ||
          line?.unitOfMeasurement ||
          line?.unit ||
          (po as any).uom ||
          '-',

        /*
         * Keep estimatedUnitPrice aligned with the original vendor price.
         * Never divide the PO total across lines.
         */
        estimatedUnitPrice: originalUnitPrice,

        originalUnitPrice,
        companyUnitPrice:
          line?.companyUnitPrice ??
          null,

        originalLineSubtotal,
        originalLineDiscount:
          line?.originalLineDiscount ??
          null,
        originalLineTax,
        originalLineTotal,

        companyLineSubtotal,
        companyLineDiscount:
          line?.companyLineDiscount ??
          null,
        companyLineTax,
        companyLineTotal,

        /*
         * Preserve selected quotation snapshot values because downstream
         * rendering may use them as fallbacks.
         */
        selectedOriginalSubtotal:
          line?.selectedOriginalSubtotal ??
          null,
        selectedOriginalTax:
          line?.selectedOriginalTax ??
          null,
        selectedOriginalTotal:
          line?.selectedOriginalTotal ??
          null,

        selectedCompanySubtotal:
          line?.selectedCompanySubtotal ??
          null,
        selectedCompanyTax:
          line?.selectedCompanyTax ??
          null,
        selectedCompanyTotal:
          line?.selectedCompanyTotal ??
          null,

        quotationCurrencyCode:
          line?.quotationCurrencyCode ??
          po.quotationCurrency ??
          po.currency ??
          null,

        companyCurrencyCode:
          line?.companyCurrencyCode ??
          po.companyCurrencyCode ??
          null,

        lockedExchangeRate:
          line?.lockedExchangeRate ??
          po.exchangeRate ??
          null,

        discountPercent:
          line?.discountPercent ??
          reqLine?.discountPercent ??
          0,

        gstPercent:
          line?.gstPercent ??
          reqLine?.gstPercent ??
          0,

        notes:
          line?.remarks ??
          reqLine?.notes ??
          null,

        part:
          reqLine?.part ??
          line?.part ??
          null,

        subcomponent:
          reqLine?.subcomponent ??
          line?.subcomponent ??
          null,

        subcomponentName:
          reqLine?.subcomponent?.name ??
          reqLine?.subcomponentName ??
          line?.subcomponentName ??
          null,

        equipmentName:
          reqLine?.subcomponent?.equipmentName ??
          reqLine?.equipmentName ??
          line?.equipmentName ??
          null,
      } as RequisitionLine
    })
  }

  /*
   * Legacy/fallback path:
   * Requisition lines exist but quotation lines were not returned as the
   * primary list. Merge by requisition-line ID.
   */
  const quotationLineMap = new Map<number, any>()

  ;(po.quotationLines || []).forEach((line: any) => {
    if (line?.requisitionLineId != null) {
      quotationLineMap.set(
        Number(line.requisitionLineId),
        line
      )
    }
  })

  if (
    Array.isArray(requisitionLines) &&
    requisitionLines.length > 0
  ) {
    return requisitionLines.map((line: any) => {
      const quoteLine =
        quotationLineMap.get(Number(line.id)) ||
        null

      const originalUnitPrice =
        quoteLine?.originalUnitPrice ??
        quoteLine?.unitPrice ??
        line?.estimatedUnitPrice ??
        null

      return {
        ...line,

        quantity:
          quoteLine?.assignedQuantity ??
          quoteLine?.quantity ??
          line?.quantity ??
          0,

        estimatedUnitPrice: originalUnitPrice,

        originalUnitPrice,

        companyUnitPrice:
          quoteLine?.companyUnitPrice ??
          null,

        originalLineSubtotal:
          quoteLine?.originalLineSubtotal ??
          quoteLine?.selectedOriginalSubtotal ??
          null,

        originalLineDiscount:
          quoteLine?.originalLineDiscount ??
          null,

        originalLineTax:
          quoteLine?.originalLineTax ??
          quoteLine?.selectedOriginalTax ??
          null,

        originalLineTotal:
          quoteLine?.originalLineTotal ??
          quoteLine?.selectedOriginalTotal ??
          null,

        companyLineSubtotal:
          quoteLine?.companyLineSubtotal ??
          quoteLine?.selectedCompanySubtotal ??
          null,

        companyLineDiscount:
          quoteLine?.companyLineDiscount ??
          null,

        companyLineTax:
          quoteLine?.companyLineTax ??
          quoteLine?.selectedCompanyTax ??
          null,

        companyLineTotal:
          quoteLine?.companyLineTotal ??
          quoteLine?.selectedCompanyTotal ??
          null,

        selectedOriginalSubtotal:
          quoteLine?.selectedOriginalSubtotal ??
          null,

        selectedOriginalTax:
          quoteLine?.selectedOriginalTax ??
          null,

        selectedOriginalTotal:
          quoteLine?.selectedOriginalTotal ??
          null,

        selectedCompanySubtotal:
          quoteLine?.selectedCompanySubtotal ??
          null,

        selectedCompanyTax:
          quoteLine?.selectedCompanyTax ??
          null,

        selectedCompanyTotal:
          quoteLine?.selectedCompanyTotal ??
          null,

        quotationCurrencyCode:
          quoteLine?.quotationCurrencyCode ??
          po.quotationCurrency ??
          po.currency ??
          null,

        companyCurrencyCode:
          quoteLine?.companyCurrencyCode ??
          po.companyCurrencyCode ??
          null,

        lockedExchangeRate:
          quoteLine?.lockedExchangeRate ??
          po.exchangeRate ??
          null,

        discountPercent:
          quoteLine?.discountPercent ??
          line?.discountPercent ??
          0,

        gstPercent:
          quoteLine?.gstPercent ??
          line?.gstPercent ??
          0,

        notes:
          quoteLine?.remarks ??
          line?.notes ??
          null,
      } as RequisitionLine
    })
  }

  /*
   * Additional defensive path for legacy payloads containing only quotation
   * lines but no nested requisition lines.
   */
  if (
    Array.isArray(po.quotationLines) &&
    po.quotationLines.length > 0
  ) {
    return po.quotationLines.map(
      (line: any, idx: number) => {
        const originalUnitPrice =
          line?.originalUnitPrice ??
          line?.unitPrice ??
          null

        return {
          id:
            line?.requisitionLineId ??
            idx + 1,

          manualItemName:
            line?.lineItemName ||
            line?.manualItemName ||
            `Line ${idx + 1}`,

          quantity:
            line?.assignedQuantity ??
            line?.quantity ??
            0,

          unitOfMeasurement:
            line?.unitOfMeasurement ||
            line?.unit ||
            (po as any).uom ||
            '-',

          estimatedUnitPrice: originalUnitPrice,

          originalUnitPrice,

          companyUnitPrice:
            line?.companyUnitPrice ??
            null,

          originalLineSubtotal:
            line?.originalLineSubtotal ??
            line?.selectedOriginalSubtotal ??
            null,

          originalLineDiscount:
            line?.originalLineDiscount ??
            null,

          originalLineTax:
            line?.originalLineTax ??
            line?.selectedOriginalTax ??
            null,

          originalLineTotal:
            line?.originalLineTotal ??
            line?.selectedOriginalTotal ??
            null,

          companyLineSubtotal:
            line?.companyLineSubtotal ??
            line?.selectedCompanySubtotal ??
            null,

          companyLineDiscount:
            line?.companyLineDiscount ??
            null,

          companyLineTax:
            line?.companyLineTax ??
            line?.selectedCompanyTax ??
            null,

          companyLineTotal:
            line?.companyLineTotal ??
            line?.selectedCompanyTotal ??
            null,

          selectedOriginalSubtotal:
            line?.selectedOriginalSubtotal ??
            null,

          selectedOriginalTax:
            line?.selectedOriginalTax ??
            null,

          selectedOriginalTotal:
            line?.selectedOriginalTotal ??
            null,

          selectedCompanySubtotal:
            line?.selectedCompanySubtotal ??
            null,

          selectedCompanyTax:
            line?.selectedCompanyTax ??
            null,

          selectedCompanyTotal:
            line?.selectedCompanyTotal ??
            null,

          quotationCurrencyCode:
            line?.quotationCurrencyCode ??
            po.quotationCurrency ??
            po.currency ??
            null,

          companyCurrencyCode:
            line?.companyCurrencyCode ??
            po.companyCurrencyCode ??
            null,

          lockedExchangeRate:
            line?.lockedExchangeRate ??
            po.exchangeRate ??
            null,

          discountPercent:
            line?.discountPercent ??
            0,

          gstPercent:
            line?.gstPercent ??
            0,

          notes:
            line?.remarks ??
            null,

          part:
            line?.part ??
            null,

          subcomponent:
            line?.subcomponent ??
            null,
        } as RequisitionLine
      }
    )
  }

  /*
   * Final single-line fallback.
   * Use only explicit PO-level values—never derive a price by dividing totals.
   */
  return [
    {
      id: 1,

      manualItemName:
        po.description ||
        'PO Item',

      quantity:
        po.quantity ??
        0,

      unitOfMeasurement:
        (po as any).uom ||
        '-',

      estimatedUnitPrice:
        po.unitPrice ??
        null,

      originalUnitPrice:
        po.unitPrice ??
        null,

      companyUnitPrice:
        null,

      originalLineSubtotal:
        null,

      originalLineTotal:
        null,

      companyLineSubtotal:
        null,

      companyLineTotal:
        null,

      discountPercent: 0,
      gstPercent: 0,
    } as RequisitionLine,
  ]
}
const PurchaseOrderPARUDocument: FC<PurchaseOrderPARUDocumentProps> = ({
  purchaseOrder,
  vendorLogisticsChangeRequestContent,
}) => {
  const {currentUser} = useAuth()
  const isVendorView = String(currentUser?.role?.roleType || currentUser?.roleType || '').toUpperCase() === 'VENDOR'
  const lines = useMemo(() => resolveLines(purchaseOrder), [purchaseOrder])
  const parsedQuotationTerms = parseQuotationTerms(purchaseOrder.terms)
  const money = resolvePurchaseOrderMoneyPresentation(purchaseOrder, isVendorView)
  const currency = resolvePurchaseOrderOriginalCurrency(purchaseOrder)
  const poDate = purchaseOrder.poDate || purchaseOrder.deliveryDate || '-'
  const requisitionDateRaw =
    (purchaseOrder as any)?.rfq?.requisition?.createdAt ||
    (purchaseOrder as any)?.rfq?.requisition?.submittedAt ||
    null
  const requisitionDate = requisitionDateRaw ? String(requisitionDateRaw).slice(0, 10) : '-'
  const vesselName = getVesselNameFromEntity((purchaseOrder as any)?.rfq?.requisition) || '-'
  const companyName = getCompanyNameFromEntity(purchaseOrder as any) || 'Company'
  const equipmentSummary = uniqueJoinedValues(lines.map((line: any) => getEquipmentNameFromLine(line)))
  const subcomponentSummary = uniqueJoinedValues(lines.map((line: any) => getSubcomponentNameFromLine(line)))
  const requisitionAny = (purchaseOrder as any)?.rfq?.requisition || {}
  const context = resolveRequisitionViewContext(
    requisitionAny?.requestFor || (purchaseOrder as any)?.requestFor,
    requisitionAny?.category?.name
  )
  const isTechnical = context === 'TECHNICAL'
  const {primary: contextPrimaryLabel, secondary: contextSecondaryLabel} =
    getContextHeaderLabels(context)
  const departmentName =
    requisitionAny?.departmentName ||
    requisitionAny?.inventoryItemHead?.name ||
    requisitionAny?.inventoryItemHead ||
    '-'
  const subDepartmentName =
    requisitionAny?.subDepartmentName ||
    requisitionAny?.inventoryItemSubHead?.name ||
    requisitionAny?.inventoryItemSubHead ||
    '-'
  const categoryName = requisitionAny?.category?.name || requisitionAny?.categoryName || '-'
  const subCategoryName =
    requisitionAny?.inventoryItemSubCategory?.name ||
    requisitionAny?.inventoryItemSubCategoryName ||
    requisitionAny?.inventoryItemSubHead?.name ||
    requisitionAny?.inventoryItemSubHeadName ||
    requisitionAny?.subDepartmentName ||
    '-'
  const contextPrimaryValue = isTechnical
    ? equipmentSummary
    : context === 'SERVICES'
    ? departmentName
    : categoryName
  const contextSecondaryValue = isTechnical
    ? subcomponentSummary
    : context === 'SERVICES'
    ? subDepartmentName
    : subCategoryName
  const issuedByCompany =
    purchaseOrder.poRaisedByCompany ||
    (purchaseOrder as any)?.issuedByCompany ||
    companyName ||
    '-'
  const issuedByUser =
    purchaseOrder.poRaisedByUser ||
    (purchaseOrder as any)?.issuedByUser ||
    '-'
  const invoiceAddress =
    purchaseOrder.invoiceAddressedTo ||
    (purchaseOrder as any)?.invoiceAddress ||
    '-'
  const requisitionFromRfq = (purchaseOrder as any)?.rfq?.requisition
  const resolvedRequisitionCode =
    purchaseOrder.requisitionNo ||
    requisitionFromRfq?.requisition_code ||
    requisitionFromRfq?.title ||
    '-'
  const linkedDefectDisplay = getLinkedDefectDisplay(requisitionAny) || '-'
  const resolvedApprover =
    purchaseOrder.secondApproverUsername ||
    (purchaseOrder as any)?.ownerActionByUsername ||
    (purchaseOrder as any)?.approver ||
    purchaseOrder.poRaisedByUser ||
    '-'
  const resolvedPoTerms =
    purchaseOrder.purchaseOrderTermsAndConditions ||
    purchaseOrder.terms ||
    parsedQuotationTerms.remainingNotes ||
    '-'
  const resolvedPaymentTerms =
    purchaseOrder.paymentTerms ||
    purchaseOrder.termsOfPayment ||
    parsedQuotationTerms.termsOfPayment ||
    '-'
  const resolvedRemittanceInstructions =
    (purchaseOrder as any).remittanceInstructions ||
    parsedQuotationTerms.bankDetails ||
    '-'
  const resolvedSupplierInstructions =
    purchaseOrder.specialInstructions ||
    '-'
  const resolvedDeliveryTerms =
    purchaseOrder.deliveryTerms ||
    parsedQuotationTerms.termsOfDelivery ||
    '-'
  const resolvedDeliveryInstructions =
    purchaseOrder.specialInstructions ||
    purchaseOrder.locationOfDelivery ||
    purchaseOrder.deliveryTerms ||
    parsedQuotationTerms.locationOfDelivery ||
    '-'

  const vendorObj = (purchaseOrder.vendor || (purchaseOrder as any).vendorDetails || {}) as any
  const cgaObj =
    ((purchaseOrder as any)?.rfq?.requisition?.vessel?.companyGroupAdmin ||
      (purchaseOrder as any)?.companyGroupAdmin ||
      (purchaseOrder as any)?.companyAdmin?.companyGroupAdmin ||
      (purchaseOrder as any)?.cga ||
      (purchaseOrder as any)?.company ||
      {}) as any

  const vendorGst =
    vendorObj.gstNumber ||
    vendorObj.gstin ||
    vendorObj.taxId ||
    vendorObj.gst ||
    (purchaseOrder as any).vendorGstNumber ||
    (purchaseOrder as any).vendorGst ||
    '-'
  const vendorPan =
    vendorObj.panNumber ||
    vendorObj.pan ||
    (purchaseOrder as any).vendorPanNumber ||
    (purchaseOrder as any).vendorPan ||
    '-'
  const vendorBankName =
    vendorObj.bankName ||
    vendorObj.bankDetails?.bankName ||
    (purchaseOrder as any).vendorBankName ||
    (purchaseOrder as any).bankName ||
    '-'
  const vendorAccountNo =
    vendorObj.bankAccountNumber ||
    vendorObj.accountNumber ||
    vendorObj.bankAccountNo ||
    vendorObj.bankDetails?.accountNumber ||
    (purchaseOrder as any).vendorAccountNumber ||
    (purchaseOrder as any).accountNumber ||
    '-'
  const vendorIfsc =
    vendorObj.bankIfscCode ||
    vendorObj.bankSwiftCode ||
    vendorObj.ifscCode ||
    vendorObj.swiftCode ||
    vendorObj.ifsc ||
    vendorObj.swift ||
    vendorObj.bankDetails?.ifscCode ||
    (purchaseOrder as any).vendorIfscCode ||
    (purchaseOrder as any).ifscCode ||
    '-'

  const companyGst =
    cgaObj.gstNumber ||
    (purchaseOrder as any).companyGstNumber ||
    (purchaseOrder as any).companyGst ||
    (purchaseOrder as any).cgaGstNumber ||
    (purchaseOrder as any).gstNumber ||
    '-'
  const companyPan =
    cgaObj.panNumber ||
    (purchaseOrder as any).companyPanNumber ||
    (purchaseOrder as any).companyPan ||
    (purchaseOrder as any).cgaPanNumber ||
    (purchaseOrder as any).panNumber ||
    '-'
  const companyVat =
    cgaObj.vatNumber ||
    (purchaseOrder as any).companyVatNumber ||
    (purchaseOrder as any).companyVat ||
    (purchaseOrder as any).cgaVatNumber ||
    (purchaseOrder as any).vatNumber ||
    '-'

  const billingAddressDisplay =
    purchaseOrder.billingAddressLabel ||
    purchaseOrder.billingAddressDisplay ||
    '-'
  
  const shippingAddressDisplay =
    purchaseOrder.shippingAddressLabel ||
    purchaseOrder.shippingAddressDisplay ||
    '-'
const vendorCurrency =
  String(
    purchaseOrder.quotationCurrency ||
    purchaseOrder.currency ||
    ''
  )
    .trim()
    .toUpperCase()

  const computedLineOriginalSubtotal = sumFiniteNumbers(lines.map((line: any) => {
    const unitPrice = firstFiniteNumber(line.originalUnitPrice, line.unitPrice, line.estimatedUnitPrice)
    if (unitPrice == null) {
      return null
    }
    return numberOrZero(line.quantity) * unitPrice
  }))
  const totalItemPrice =
    firstFiniteNumber(
      (purchaseOrder as any).originalSubtotal,
      sumFiniteNumbers(lines.map((line: any) => line.originalLineSubtotal)),
      sumFiniteNumbers(lines.map((line: any) => line.selectedOriginalSubtotal)),
      sumFiniteNumbers(lines.map((line: any) => line.originalLineTotal)),
      computedLineOriginalSubtotal
    )
  const freightCost = numberOrZero(purchaseOrder.freightCost ?? parsedQuotationTerms.freightCost)
  const packagingCost = numberOrZero(purchaseOrder.packagingCost ?? parsedQuotationTerms.packagingCost)
  const truckCost = numberOrZero(purchaseOrder.truckCost ?? parsedQuotationTerms.truckCost)
  const bargeCost = numberOrZero(purchaseOrder.bargeCost ?? parsedQuotationTerms.bargeCost)
  const otherCost = numberOrZero(purchaseOrder.otherCost ?? parsedQuotationTerms.otherCost)
  const vatOnLogistics = numberOrZero(purchaseOrder.vatOnLogistics ?? parsedQuotationTerms.vatOnLogistics)
  const logistics = freightCost + packagingCost + truckCost + bargeCost + otherCost
  // Recalculate from the persisted component values so an approved vendor
  // logistics change is reflected immediately even if an older aggregate
  // field is still present on the response.
  const logisticsTotal = logistics + vatOnLogistics
  const discount = numberOrZero(purchaseOrder.totalDiscount)
  const netAmount =
    firstFiniteNumber(
      (purchaseOrder as any).originalTotal,
      purchaseOrder.totalAmount,
      totalItemPrice != null ? totalItemPrice + logistics + vatOnLogistics - discount : null
    )

  // The left side is always the document/PO currency. Company currency is
  // shown only in the currency-details panel and as a converted secondary value.
  const displayCurrency = money.originalCurrency || currency
  const displayNetAmount = netAmount
  const companySubtotal = readFiniteNumber((purchaseOrder as any).companySubtotal)
  const companyNetAmount = money.hasCompanySnapshot ? money.companyAmount : null
  const displayAmount = (originalValue: number, _companyValue?: number | null) => originalValue
  const optionalCommercialRows = [
    ['Trucking/Freight Cost', freightCost],
    ['Pkg & Handling Cost', packagingCost],
    ['Truck Cost', truckCost],
    ['Barge/Workboat Cost', bargeCost],
    ['Other Cost', otherCost],
    ['VAT/GST on Logistic/Other', vatOnLogistics],
    ['Logistics Total', logisticsTotal],
    ['Discount on Total Items', discount],
  ].filter(([, value]) => isMeaningfulFinancialValue(value))
  const displayRate = money.hasCompanySnapshot
    ? money.exchangeRateDisplay || (money.sameCurrency ? `1 ${vendorCurrency} = 1 ${displayCurrency}` : '-')
    : money.companyCurrency && !money.sameCurrency
      ? 'Not configured'
      : 'Company currency conversion unavailable'
  const {logoSrc: companyLogoUrl, watermarkSrc: companyWatermarkUrl, ready: brandingReady} =
    useProcurementBrandingAssets(
      (purchaseOrder as any)?.companyLogoUrl ||
        (purchaseOrder as any)?.logoUrl ||
        (purchaseOrder as any)?.company?.logoUrl ||
        (purchaseOrder as any)?.rfq?.requisition?.companyLogoUrl ||
        (purchaseOrder as any)?.rfq?.requisition?.logoUrl ||
        (purchaseOrder as any)?.rfq?.requisition?.company?.logoUrl ||
        (purchaseOrder as any)?.rfq?.requisition?.category?.caid?.logoUrl ||
        (purchaseOrder as any)?.rfq?.requisition?.category?.cgaid?.logoUrl,
      (purchaseOrder as any)?.companyWatermarkUrl ||
        (purchaseOrder as any)?.watermarkUrl ||
        (purchaseOrder as any)?.company?.watermarkUrl ||
        (purchaseOrder as any)?.rfq?.requisition?.companyWatermarkUrl ||
        (purchaseOrder as any)?.rfq?.requisition?.watermarkUrl ||
        (purchaseOrder as any)?.rfq?.requisition?.company?.watermarkUrl ||
        (purchaseOrder as any)?.rfq?.requisition?.category?.caid?.watermarkUrl ||
        (purchaseOrder as any)?.rfq?.requisition?.category?.cgaid?.watermarkUrl,
      (purchaseOrder as any)?.rfq?.requisition?.category?.cgaid
    )
  const rootClassName = `procurement-doc paru-po-doc${companyWatermarkUrl ? ' procurement-has-watermark' : ''}`

  return (
    <div className={rootClassName} data-export-ready={brandingReady ? 'true' : 'false'}>
      {companyWatermarkUrl ? (
        <div className='procurement-doc-watermark' aria-hidden='true'>
          <img
            src={companyWatermarkUrl}
            alt=''
            className='procurement-doc-watermark-image'
            crossOrigin='anonymous'
            data-pdf-watermark-source='true'
          />
        </div>
      ) : null}
      <div className='procurement-doc-content'>
        {companyLogoUrl ? (
          <div className='procurement-doc-brand-header mb-3'>
            <img
              src={companyLogoUrl}
              alt='Company Logo'
              className='procurement-doc-brand-logo'
              crossOrigin='anonymous'
              data-pdf-logo='true'
            />
          </div>
        ) : null}

      <DocBlock title='Purchase Order Header'>
        <DocFieldGrid columns={3}>
          <DocField label='Company Header' value={companyName} />
          <DocField label='Purchase Order' value={purchaseOrder.poNumber} />
          <DocField label='PO Title' value={purchaseOrder.description || 'Purchase Order'} />
        </DocFieldGrid>
      </DocBlock>

      <DocBlock title='Vendor / Company Details'>
        <DocFieldGrid columns={2}>
          <DocField label='Vendor Name' value={purchaseOrder.vendor?.name || '-'} />
          <DocField label='Issued By Company' value={issuedByCompany} />
          <DocField label='Vendor Address' value={purchaseOrder.vendorAddress} />
          <DocField label='Issued By User' value={issuedByUser} />
          <DocField
            label='Vendor Contact'
            value={`${purchaseOrder.vendor?.email || '-'} / ${purchaseOrder.vendor?.contactNo || '-'}`}
          />
          <DocField label='Invoice Addressed To' value={invoiceAddress} />
          <DocField label='Billing Address' value={billingAddressDisplay} />
          <DocField label='Shipping Address' value={shippingAddressDisplay} />
        </DocFieldGrid>
      </DocBlock>

      <DocBlock title='Other Details'>
        <DocFieldGrid columns={2}>
          <DocField label='Vendor GST / Tax ID' value={vendorGst} />
          <DocField label='Company GST Number' value={companyGst} />
          <DocField label='Vendor PAN Number' value={vendorPan} />
          <DocField label='Company PAN Number' value={companyPan} />
          <DocField label='Vendor Bank Name' value={vendorBankName} />
          <DocField label='Company VAT Number' value={companyVat} />
          <DocField label='Vendor Account Number' value={vendorAccountNo} />
          <DocField label='-' value='' />
          <DocField label='Vendor IFSC / Swift Code' value={vendorIfsc} />
          <DocField label='-' value='' />
        </DocFieldGrid>
      </DocBlock>

      <DocBlock title='Order Particulars'>
        <DocFieldGrid columns={4}>
          <DocField label='Vessel Name' value={vesselName} />
          <DocField label={contextPrimaryLabel} value={contextPrimaryValue} />
          <DocField label={contextSecondaryLabel} value={contextSecondaryValue} />
          <DocField label='Requisition Code' value={resolvedRequisitionCode} />
          <DocField label='Requisition Date' value={requisitionDate} />
          <DocField label='Linked Defect' value={linkedDefectDisplay} />
          <DocField label='PO Code' value={purchaseOrder.poNumber} />
          <DocField label='Job Number' value={(purchaseOrder as any).jobNumber || '-'} />
          <DocField label='PO Date' value={poDate} />
          <DocField label='PO Currency' value={currency} />
          <DocField label='Supplier Reference' value={purchaseOrder.supplierReference} />
          <DocField
  label='Budget Account Code'
  value={purchaseOrder.budgetCode || '-'}
/>

<DocField
  label='Budget Sub Account'
  value={(purchaseOrder as any).budgetSubaccountCode || '-'}
/>

<DocField
  label='Logistics Account Code'
  value={purchaseOrder.logisticAccountCode || '-'}
/>

<DocField
  label='Logistics Sub Account'
  value={purchaseOrder.logisticSubaccountCode || '-'}
/>
          <DocField label='Approver' value={resolvedApprover} />
          <DocField label='Delivery Port' value={purchaseOrder.locationOfDelivery || '-'} />
          <DocField label='Delivery Date' value={purchaseOrder.deliveryDate || '-'} />
          <DocField label='Status' value={purchaseOrder.status} />
        </DocFieldGrid>
      </DocBlock>

      <DocBlock title='Item Grid'>
        <div className='table-responsive'>
          <table className='table table-bordered align-middle doc-tight procurement-print-table'>
            <thead className='table-light'>
              <tr>
                <th style={{width: '56px'}}>No</th>
                <th>Item Description</th>
                <th style={{width: '88px'}}>Qty</th>
                <th style={{width: '90px'}}>Unit</th>
                <th style={{width: '120px'}}>Unit Price</th>
                <th style={{width: '130px'}}>Total</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, idx) => {
                const qty = numberOrZero(line.quantity)
const originalUnitPrice = firstFiniteNumber(
  (line as any).originalUnitPrice,
  (line as any).unitPrice,
  line.estimatedUnitPrice
)
                const companyUnitPrice = readFiniteNumber((line as any).companyUnitPrice)
                const discPercent = numberOrZero((line as any).discountPercent)
                const gstPercent = numberOrZero((line as any).gstPercent)
                const originalSubtotal = firstFiniteNumber(
                  (line as any).originalLineSubtotal,
                  (line as any).selectedOriginalSubtotal,
                  originalUnitPrice != null ? qty * originalUnitPrice : null
                )
                const total = firstFiniteNumber(
                  (line as any).originalLineTotal,
                  (line as any).selectedOriginalTotal,
                  calculateLineTotal(originalSubtotal, discPercent, gstPercent)
                )
                const companyTotal = readFiniteNumber((line as any).companyLineTotal)
              return (
                <React.Fragment key={`po-line-${idx}`}>
                    <tr className='procurement-avoid-break'>
                      <td>{idx + 1}</td>
                      <td>
                        <div>{getItemDisplayNameFromLine(line, idx)}</div>
                        <div className='procurement-line-detail'>
                          {isTechnical
                            ? `Part: ${(line as any).part?.name || '-'} | Part No: ${
                                (line as any).partNumber || (line as any).part?.partNo || (line as any).part?.code || '-'
                              } | Drawing: ${(line as any).drawingNumber || (line as any).part?.drawNo || '-'}`
                            : `${contextPrimaryLabel}: ${contextPrimaryValue} | ${contextSecondaryLabel}: ${contextSecondaryValue}`}
                        </div>
                        {isTechnical ? (
                          <div className='procurement-line-detail'>
                            {contextPrimaryLabel}: {getEquipmentNameFromLine(line) || '-'} | {contextSecondaryLabel}:{' '}
                            {getSubcomponentNameFromLine(line) || '-'}
                          </div>
                        ) : null}
                        {resolveLineAccountCode(purchaseOrder, line as any) ? (
                          <div className='procurement-line-detail'>
                            Account: {resolveLineAccountCode(purchaseOrder, line as any)}
                          </div>
                        ) : null}
                        {(line as any).notes ? (
                          <div className='procurement-line-note'>
                            <span className='text-muted fw-semibold'>Remarks:</span> {(line as any).notes}
                          </div>
                        ) : null}
                      </td>
                      <td>{qty}</td>
                      <td>{line.unitOfMeasurement || '-'}</td>
                      <td>
                        {originalUnitPrice != null ? (
                          <MoneyDisplay
                            compact
                            primaryAmount={originalUnitPrice}
                            primaryCurrency={money.originalCurrency}
                            secondaryAmount={
                              companyUnitPrice != null && money.companyCurrency && !money.sameCurrency
                                ? companyUnitPrice
                                : null
                            }
                            secondaryCurrency={
                              companyUnitPrice != null && money.companyCurrency && !money.sameCurrency
                                ? money.companyCurrency
                                : null
                            }
                            exchangeRateDisplay={
                              companyUnitPrice != null ? money.exchangeRateDisplay : null
                            }
                            sameCurrency={money.sameCurrency}
                          />
                        ) : (
                          <span>-</span>
                        )}
                      </td>
                      <td>
                        {total != null ? (
                          <MoneyDisplay
                            compact
                            primaryAmount={total}
                            primaryCurrency={money.originalCurrency}
                            secondaryAmount={
                              companyTotal != null && money.companyCurrency && !money.sameCurrency
                                ? companyTotal
                                : null
                            }
                            secondaryCurrency={
                              companyTotal != null && money.companyCurrency && !money.sameCurrency
                                ? money.companyCurrency
                                : null
                            }
                            exchangeRateDisplay={
                              companyTotal != null ? money.exchangeRateDisplay : null
                            }
                            sameCurrency={money.sameCurrency}
                          />
                        ) : (
                          <span>-</span>
                        )}
                      </td>
                    </tr>
                    
                </React.Fragment>
              )
            })}
          </tbody>
          </table>
        </div>
      </DocBlock>

      <DocBlock title='Commercial Summary'>
        <div className='row g-4'>
          <div className='col-md-6'>
            <table className='table table-sm table-borderless mb-0'>
              <tbody>
                <tr>
                  <td>Total Item Price</td>
                  <td className='text-end'>
                    <MoneyDisplay
                      compact
                      primaryAmount={displayAmount(totalItemPrice ?? 0, companySubtotal)}
                      primaryCurrency={displayCurrency}
                      sameCurrency
                    />
                  </td>
                </tr>
                {optionalCommercialRows.map(([label, value]) => (
                  <tr key={String(label)}>
                    <td>{label}</td>
                    <td className='text-end'>
                      {formatFallbackMoney(displayAmount(value as number, null), displayCurrency)}
                    </td>
                  </tr>
                ))}
                <tr className='border-top'>
                  <td className='fw-bold'>Net Amount</td>
                  <td className='text-end fw-bold'>
                        <MoneyDisplay
                      compact
                      primaryAmount={displayNetAmount}
                      primaryCurrency={displayCurrency}
                      sameCurrency
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className='col-md-6'>
            <DocNote title='Currency Details'>
              Document Currency: {vendorCurrency || '-'}
              {'\n'}
              Company Currency: {money.companyCurrency || '-'}
              {'\n'}
              Exchange Rate Used: {displayRate}
              {'\n'}
              Original Net Amount: {formatFallbackMoney(netAmount, money.originalCurrency)}
              {'\n'}
              Company Net Amount: {formatFallbackMoney(companyNetAmount, money.companyCurrency)}
              {money.companyCurrency && !money.sameCurrency && !money.hasCompanySnapshot
                ? `\nNo exchange rate configured for ${money.originalCurrency} to ${money.companyCurrency}.`
                : ''}
              {(purchaseOrder as any).amountInWords ? `\nAmount in Words: ${(purchaseOrder as any).amountInWords}` : ''}
            </DocNote>
          </div>
        </div>
      </DocBlock>

      {vendorLogisticsChangeRequestContent}

      <DocBlock title='Terms, Instructions & Expectations'>
        <DocFieldGrid columns={2}>
          <DocNote title='Terms & Conditions'>{resolvedPoTerms}</DocNote>
          <DocNote title='Payment Terms'>{resolvedPaymentTerms}</DocNote>
          <DocNote title='Remittance Instructions'>{resolvedRemittanceInstructions}</DocNote>
          <DocNote title='Supplier Instructions'>{resolvedSupplierInstructions}</DocNote>
          <DocNote title='Delivery Terms'>{resolvedDeliveryTerms}</DocNote>
          <DocNote title='Delivery Instructions'>{resolvedDeliveryInstructions}</DocNote>
        </DocFieldGrid>
      </DocBlock>

      <DocBlock title='Attachments'>
        <ProcurementAttachmentPrintList
          attachments={((purchaseOrder as any)?.attachments || []).map((attachment: any) => ({
            id: attachment?.id ?? null,
            registryId: attachment?.registryId ?? attachment?.id ?? null,
            attachmentKey: attachment?.attachmentKey ?? null,
            fileName: attachment?.fileName || attachment?.name || 'Attachment',
            url: attachment?.url || attachment?.path || '',
            viewUrl: attachment?.viewUrl || null,
            downloadUrl: attachment?.downloadUrl || null,
            sizeBytes: attachment?.sizeBytes ?? attachment?.size ?? null,
            available: attachment?.available ?? null,
          }))}
        />
      </DocBlock>
      </div>
    </div>
  )
}

export default PurchaseOrderPARUDocument
